import { FC, useEffect, useState } from "react";
import { CarDataType } from "data/types";
import TabFilters from "./TabFilters";
import FleetCard from "components/FleetCard/FleetCard";
import { useNavigate } from "react-router-dom";
import { useData } from "data/data-provider";
import { CarListingModal } from "models/vehicleModal";

export interface SectionGridFilterCardProps {
  className?: string;
  data?: CarDataType[];
}

const SectionGridFilterCard: FC<SectionGridFilterCardProps> = ({
  className = "",
  data,
}) => {
  const { ourVehicles, ourVehiclesLoading, setSelectedCar } = useData();
  const [visibleCarsCount, setVisibleCarsCount] = useState<number>(8);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedMakes, setSelectedMakes] = useState<string[]>([]);
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [sortOrder, setSortOrder] = useState<string>("default");
  const [maxPrice, setMaxPrice] = useState(0);
  const [filteredFleet, setFilteredFleet] = useState<CarListingModal[]>(
    ourVehicles || []
  );
  const [rangePrices, setRangePrices] = useState([0, maxPrice]);
  const navigate = useNavigate();

  useEffect(() => {
    if (ourVehicles && ourVehicles.length > 0) {
      const calculatedMaxPrice = ourVehicles.reduce(
        (acc, current) => Math.max(acc, current.price),
        0
      );
      setMaxPrice(calculatedMaxPrice);
    }
  }, [ourVehicles]);

  useEffect(() => {
    if (maxPrice > 0) {
      setRangePrices([0, maxPrice]);
    }
  }, [maxPrice]);

  const getUniqueFleetClasses = () => {
    return Array.from(
      new Set(ourVehicles?.map((car) => car?.fleet_class?.name ?? "") || [])
    ).filter((item): item is string => item !== undefined && item !== "");
  };

  const getUniqueMakes = () => {
    return Array.from(
      new Set(ourVehicles?.map((car) => car?.make ?? "") || [])
    ).filter((item): item is string => item !== undefined && item !== "");
  };

  const getUniqueModels = () => {
    return Array.from(
      new Set(ourVehicles?.map((car) => car?.model ?? "") || [])
    ).filter((item): item is string => item !== undefined && item !== "");
  };

  useEffect(() => {
    if (ourVehicles) {
      // Apply all filters
      let filtered = [...ourVehicles];

      // Filter by price range
      filtered = filtered.filter(
        (item) => item.price >= rangePrices[0] && item.price <= rangePrices[1]
      );

      // Filter by fleet class
      if (
        selectedTypes.length > 0 &&
        selectedTypes.length < getUniqueFleetClasses().length
      ) {
        filtered = filtered.filter((item: CarListingModal) =>
          selectedTypes.includes(item?.fleet_class?.name ?? "")
        );
      }

      // Filter by make
      if (
        selectedMakes.length > 0 &&
        selectedMakes.length < getUniqueMakes().length
      ) {
        filtered = filtered.filter((item: CarListingModal) =>
          selectedMakes.includes(item?.make ?? "")
        );
      }

      // Filter by model
      if (
        selectedModels.length > 0 &&
        selectedModels.length < getUniqueModels().length
      ) {
        filtered = filtered.filter((item: CarListingModal) =>
          selectedModels.includes(item?.model ?? "")
        );
      }

      // Apply sorting
      if (sortOrder === "aToZ") {
        filtered.sort((a, b) => {
          const modelA = (a.make || "") + " " + (a.model || "");
          const modelB = (b.make || "") + " " + (b.model || "");
          return modelA.localeCompare(modelB);
        });
      } else if (sortOrder === "zToA") {
        filtered.sort((a, b) => {
          const modelA = (a.make || "") + " " + (a.model || "");
          const modelB = (b.make || "") + " " + (b.model || "");
          return modelB.localeCompare(modelA);
        });
      } else if (sortOrder === "priceLowToHigh") {
        filtered.sort((a, b) => a.price - b.price);
      } else if (sortOrder === "priceHighToLow") {
        filtered.sort((a, b) => b.price - a.price);
      }

      setFilteredFleet(filtered);

      // Reset expanded state when filters change
      if (filtered.length <= visibleCarsCount) {
        setExpanded(false);
      }
    }
  }, [
    ourVehicles,
    selectedTypes,
    selectedMakes,
    selectedModels,
    rangePrices,
    sortOrder,
  ]);

  const renderCard = (car: CarListingModal) => {
    if (shouldCenter) {
      return (
        <div
          key={car.id}
          className="w-full sm:w-[calc(50%-16px)] lg:w-[calc(33.333%-16px)] xl:w-[calc(25%-16px)]"
        >
          <FleetCard
            data={car}
            onClick={() => {
              navigate("/car-listing-detail?selectedCarId=" + car.id);
              setSelectedCar(car);
            }}
          />
        </div>
      );
    }
    return (
      <FleetCard
        key={car.id}
        data={car}
        onClick={() => {
          navigate("/car-listing-detail?selectedCarId=" + car.id);
          setSelectedCar(car);
        }}
      />
    );
  };
  const carsToDisplay = filteredFleet?.slice(0, visibleCarsCount);
  const shouldCenter = carsToDisplay && carsToDisplay.length < 4;

  const handleShowMore = () => {
    setVisibleCarsCount((prevCount) => prevCount + 8);
    if (filteredFleet && visibleCarsCount + 8 >= filteredFleet.length) {
      setExpanded(true);
    }
  };

  const handleShowLess = () => {
    setVisibleCarsCount(8);
    setExpanded(false);
  };

  return (
    <div
      className={`nc-SectionGridFilterCard ${className}`}
      data-nc-id="SectionGridFilterCard"
      style={{ minHeight: "80vh" }}
    >
      <div className="flex flex-col sm:flex-row justify-between align-center">
        <span className="lg:text-5xl md:text-3xl text-3xl font-semibold lg:leading-[73.14px] leading-12 text-left mb-4 text-text-color">
          Explore All Vehicles
        </span>
        <div className="mb-8 lg:mb-0 flex items-center">
          {ourVehicles && (
            <TabFilters
              types={[...getUniqueFleetClasses()]}
              makes={[...getUniqueMakes()]}
              models={[...getUniqueModels()]}
              selectedTypes={selectedTypes}
              selectedMakes={selectedMakes}
              selectedModels={selectedModels}
              setSelectedTypes={setSelectedTypes}
              setSelectedMakes={setSelectedMakes}
              setSelectedModels={setSelectedModels}
              rangePrices={rangePrices}
              setRangePrices={setRangePrices}
              maxPrice={maxPrice}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
            />
          )}
        </div>
      </div>

      <div className="nc-SectionGridFeaturePlaces relative">
        <div
          className={`grid gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4`}
        >
          {ourVehiclesLoading ? (
            <div className="flex justify-center items-center w-full">
              <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-t-transparent border-secondary-100"></div>
            </div>
          ) : (
            filteredFleet?.map((car) => renderCard(car))
          )}
        </div>
        <div className="flex mt-16 justify-center items-center">
          {/* <ButtonPrimary loading>Show me more</ButtonPrimary> */}
        </div>
      </div>
    </div>
  );
};

export default SectionGridFilterCard;
